import { Controller } from '@hotwired/stimulus';
import _throttle from 'lodash/throttle';

// Define constants for player types
const PLAYER_TYPES = {
  HTML_VIDEO: 'html-video'
};

class ActiveTimer extends Controller {
  static targets = ["active_time_report"];
  static values = {
    completionThreshold: Number,
    initialActiveTime: Number,
    userTaskId: Number
  };


  initialize() {
    this.debug = false;
    this.activeTimeIncrementInSeconds = 5;
    this.idleTimeoutInSeconds = 180;
    this.reportIntervalInSeconds = 30;

    this.registerActivity = this.registerActivity.bind(this);
    this.registerVisibilityChange = this.registerVisibilityChange.bind(this);
    this.activeEventFromIframe = this.activeEventFromIframe.bind(this);
    this.throttleRegisterActivity = _throttle(this.registerActivity, 500);

    // Initialize state variables
    this.playerOnPage = null;
    this.cumulativeActiveTime = 0;
    this.previouslyReportedActiveTime = 0;
    this.pageIsVisible = true;
    this.userIsActive = true;
    this.timerIsPaused = false;
    this.idleTimeoutTimer = null;

    // Initialize intervals
    this._idleTimeoutTimer = null;
    this._incrementTimer = null;
    this._reportTimer = null;
  }

  connect() {
    this.debug = window.location.toString().indexOf('debug') > 0;
    this.log('ActiveTimerController connected', this.initialActiveTimeValue, this.userTaskIdValue);

    // Set state variables
    this.cumulativeActiveTime = this.initialActiveTimeValue || 0;
    this.previouslyReportedActiveTime = this.initialActiveTimeValue || 0;
    this.pageIsVisible = true;
    this.userIsActive = true;
    this.timerIsPaused = false;

    this.checkPageForPlayers();
    this.initializeEventObservers();

    this.resetIdleTimeoutTimer(); // stops active time when user is idle for this long
    this.resetIncrementTimer(); // increments active time
    this.resetReportTimer(); // reports active time to server
  }

  disconnect() {
    this.log('ActiveTimerController disconnected');
    this.stopTimers();
    this.terminateEventObservers();
  }

  initializeEventObservers() {
    this.log('Adding event observers for ActiveTimerController');
    document.addEventListener('keydown', this.registerActivity);
    document.addEventListener('touchstart', this.registerActivity);
    document.addEventListener('click', this.registerActivity);
    document.addEventListener('scroll', this.throttleRegisterActivity);
    document.addEventListener('visibilitychange', this.registerVisibilityChange);

    // Listener for iframe activities. Allows the activities to notify
    // the time when an event occurred inside the activities.
    // i.e. when the user did something that indicates they are active.
    window.addEventListener("message", this.activeEventFromIframe);
  }

  terminateEventObservers() {
    this.log('Removing event observers for ActiveTimerController');
    document.removeEventListener('keydown', this.registerActivity);
    document.removeEventListener('touchstart', this.registerActivity);
    document.removeEventListener('click', this.registerActivity);
    document.removeEventListener('scroll', this.throttleRegisterActivity);
    document.removeEventListener('visibilitychange', this.registerVisibilityChange);

    window.removeEventListener("message", this.activeEventFromIframe);
  }

  stopTimers() {
    this.log('Stopping all timers');
    if (this._idleTimeoutTimer) {
      clearTimeout(this._idleTimeoutTimer);
      this._idleTimeoutTimer = null;
    }
    if (this._incrementTimer) {
      clearInterval(this._incrementTimer);
      this._incrementTimer = null;
    }
    if (this._reportTimer) {
      clearInterval(this._reportTimer);
      this._reportTimer = null;
    }
  }

  resetIdleTimeoutTimer() {
    this.log('Resetting idle timer');
    clearTimeout(this._idleTimeoutTimer);
    this._idleTimeoutTimer = setTimeout(() => {
      this.idleTimerCallback();
    }, this.idleTimeoutInSeconds * 1000);
  };

  idleTimerCallback() {
    this.log('Idle timer callback triggered');
    // We are about to timeout. Check first if there is a video playing.
    if (this.isAnyPlayerActive()) {
      this.log("ActiveTimer: idle timeout prevented due to active video playing");
      // Register activity, which will reset the idleTimer.
      this.registerActivity("video playing");
    } else {
      this.log("ActiveTimer: idleTimerCallback stopped active time");
      this.stopActiveTime();
    }
  }

  stopActiveTime() {
    this.log('Stopping active time');
    if (this.userIsActive) {
      // Transition from active to inactive
      this.userIsActive = false;
      this.log("ActiveTimer: stop active time");
      this.timerIsPaused = true;
      this.log("ActiveTimer: pause timer");

      // Display a notice to the user indicating the timer has stopped.
      const dialog = this.#constructDialog(
        // Translations set in the Participant layout
        window.I18n.tasksShow.activeTimer.title,
        window.I18n.tasksShow.activeTimer.body,
        window.I18n.tasksShow.activeTimer.button,
        () => {
          this.log("ActiveTimer: unpause timer");
          this.timerIsPaused = false;
        },
        "success"
      );
      dialog.showModal();
    }
  };

  resetIncrementTimer() {
    clearTimeout(this._incrementTimer);
    this._incrementTimer = setTimeout(() => {
      this.incrementTimerCallback();
    }, this.activeTimeIncrementInSeconds * 1000);
  };

  incrementTimerCallback() {
    if (this.userIsActive) {
      this.cumulativeActiveTime += this.activeTimeIncrementInSeconds;
    }
    this.resetIncrementTimer();
  };

  resetReportTimer() {
    // add 100 milliseconds to timeout to avoid race condition with
    // activeTimeIncrementInSeconds where we lose one
    // activeTimeIncrementInSeconds per reporting interval.
    clearTimeout(this._reportTimer);
    this._reportTimer = setTimeout(() => {
      this.reportTimerCallback();
    }, this.reportIntervalInSeconds * 1000 + 100);
  };

  reportTimerCallback() {
    if (this.previouslyReportedActiveTime != this.cumulativeActiveTime) {
      this.reportActiveTime();
    } else {
      this.log("ActiveTimer: no active time to report.");
    }
    this.resetReportTimer(this);
  };

  startActiveTime() {
    if (!this.userIsActive && !this.timerIsPaused) {
      this.userIsActive = true;
      this.log("ActiveTimer: start active time");
    }
  };

  async validateActiveTime(e) {
    this.log('Validating active time');
    if (this.cumulativeActiveTime < this.completionThresholdValue) {
      this.log_error(`ActiveTimer: Cumulative active time ${this.cumulativeActiveTime} is less than the required threshold of ${this.completionThresholdValue} seconds.`);
      const dialog = this.#constructDialog(
        // Translations set in the Participant layout
        window.I18n.tasksComplete.activeTimer.title,
        window.I18n.tasksComplete.activeTimer.body,
        window.I18n.tasksComplete.activeTimer.button,
        () => {
          this.log("ActiveTimer: validation dialog closed");
        }
      );
      dialog.showModal();

      e.preventDefault(); // Prevent form submission or link navigation

      // Report early completion
      let response;
      try {
        response = await fetch(`/user_tasks/${this.userTaskIdValue}/report_early_completion`, { method: 'PUT' });
        if (!response?.ok) {
          throw new Error(`Response status: ${response?.status}`);
        }
      } catch (error) {
        this.log_error("ActiveTimer: Error reporting early completion", error);
      }

      return false;
    }
  }

  registerActivity(activityType) {
    const logActivityType = typeof activityType === "string" ? activityType : activityType.type;

    if (this.pageIsVisible) {
      this.log(`ActiveTimer: registered activity: ${logActivityType}`);
      this.startActiveTime();
      this.resetIdleTimeoutTimer();
    }
  }

  registerVisibilityChange() {
    this.log('Visibility change detected');
    this.pageIsVisible = document.visibilityState === 'visible';

    if (document.visibilityState === 'visible') {
      this.log("ActiveTimer: page becomes visible");
    } else {
      this.log("ActiveTimer: page becomes hidden");
    }
  }

  async reportActiveTime() {
    // User has been active, report to server
    this.log(`Reporting active time: ${this.cumulativeActiveTime} seconds for user task ID: ${this.userTaskIdValue}`);

    const reportedActiveTimeInSeconds = this.cumulativeActiveTime;

    let response;
    try {
      response = await fetch(`/user_tasks/${this.userTaskIdValue}/update_active_time`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_task_id: this.userTaskIdValue,
          active_time_in_seconds: reportedActiveTimeInSeconds
        }),
      });
      if (!response?.ok) {
        throw new Error(`Response status: ${response?.status}`);
      }

      const data = await response.json();
      this.previouslyReportedActiveTime = reportedActiveTimeInSeconds;
      this.cumulativeActiveTime = data.active_time_in_seconds;
      this.log(`ActiveTimer: Reported ${reportedActiveTimeInSeconds}, received ${data.active_time_in_seconds}`);
    } catch (error) {
      this.log_error("ActiveTimer: Error reporting active time", error);
    }
  }

  activeEventFromIframe(event) {
    // Ignore events that come from windows we do not trust
    var prodOrigin = /^(http|https)?:?\/\/assets.culturalcompetency.ca/;
    var devOrigin = /^http:\/\/localhost:/;
    if (!(event.origin.match(prodOrigin) || event.origin.match(devOrigin))) {
      this.log(
        "Ignoring message from unauthorized source: " + event.origin + ".",
        event
      );
      return;
    }

    // Ignore React DevTools messages
    if (event.data && event.data.source === "react-devtools-content-script") {
      return;
    }

    // Warn unknown messages
    if (typeof event.data === "object") {
      this.log_warn("Unknown event", event);
      return;
    }

    var fullEvent = JSON.parse(event.data);
    if (fullEvent.type === "REGISTER_ACTIVE_EVENT") {
      this.registerActivity(fullEvent.message);
    } else {
      this.log_warn("Unknown event", fullEvent.type);
    }
  }

  checkPageForPlayers() {
    this.log("calling checkPageForPlayers");
    let playerType = null;
    // Detect video player
    if (document.querySelectorAll("video").length > 0) {
      playerType = PLAYER_TYPES.HTML_VIDEO;
    }

    if (playerType) {
      this.log(`ActiveTimer: detected presence of player, type is ${playerType}`);
    } else {
      this.log_warn(`ActiveTimer: failed to detect presence of acceptable player, type is ${playerType}`);
    }

    this.playerOnPage = playerType;
  };

  isAnyPlayerActive() {
    let players_active = false;

    // If the page has players (checked at start time) then check video status.
    if (this.playerOnPage === PLAYER_TYPES.HTML_VIDEO) {
      this.log(`Check if ${this.playerOnPage} is active.`);
      players_active = this.isVideoPlayerActive();
    } else {
      this.log_warn("Player is not a known type with value: " + this.playerOnPage);
    }

    this.log(`players_active value is ${players_active}`);
    return players_active;
  };

  // Check if any of the video players are actively playing a video
  isVideoPlayerActive() {
    var playerActive = false;
    document.querySelectorAll("video").forEach((player) => {
      playerActive = playerActive || (player.currentTime > 0 && !player.paused && !player.ended && player.readyState > 2);
    });
    return playerActive;
  };

  // Append active time report to form params before submit.
  appendActiveTimeReportToFormParams(e) {
    const form = e.target;

    if (!this.activeTimeReportTarget) {
      this.log_warn("ActiveTimer: No active_time_report target found in form.");

      this.activeTimeReportTarget = document.createElement("input");
      this.activeTimeReportTarget.setAttribute("type", "hidden");
      this.activeTimeReportTarget.setAttribute("name", "active_time_report");
      form.appendChild(this.activeTimeReportTarget);
    }

    this.activeTimeReportTarget.value = this.#activeTimeReportParamsAsJson();

    return true;
  };

  // Add active time report to link URL query string before following link.
  appendActiveTimeReportToLinkUrl(e) {
    const link = e.target;
    link.href = this.#updateUrlQueryString(link.href, "active_time_report", this.#activeTimeReportParamsAsJson());

    return true;
  };

  // For debug logging
  log(msg) {
    this.debug && console.log(msg); // eslint-disable-line no-console
  }

  log_warn(msg) {
    this.debug && console.warn(msg); // eslint-disable-line no-console
  }

  log_error(msg) {
    this.debug && console.error(msg);
  }

  #activeTimeReportParams() {
    return {
      user_task_id: this.userTaskIdValue,
      active_time_in_seconds: this.cumulativeActiveTime
    };
  }

  #activeTimeReportParamsAsJson() {
    return JSON.stringify(this.#activeTimeReportParams());
  }

  #constructDialog(title, body, buttonText, buttonCallback, variant = "info") {
    const dialog = document.createElement('dialog');
    dialog.classList.add('dialog');
    dialog.dataset.controller = 'dialog';
    dialog.dataset.dialogTarget = 'dialog';

    const dialogHeader = document.createElement('div');
    dialogHeader.classList.add('dialog-header');
    dialogHeader.textContent = title;

    const dialogBody = document.createElement('div');
    dialogBody.classList.add('dialog-body');
    dialogBody.setAttribute('tabindex', '-1');
    dialogBody.innerHTML = body;

    const dialogFooter = document.createElement('div');
    dialogFooter.classList.add('dialog-footer');

    const closeButtonAction = () => {
      buttonCallback();
      dialog.close();
      dialog.remove();
    };

    const closeButton = document.createElement('button');
    closeButton.classList.add('button', `button-${variant}`, 'button-sm', 'fw-bold');
    closeButton.textContent = buttonText;
    closeButton.addEventListener('click', closeButtonAction);

    dialog.addEventListener('click', function(event) {
      var rect = dialog.getBoundingClientRect();
      var isInDialog = (rect.top <= event.clientY && event.clientY <= rect.top + rect.height &&
        rect.left <= event.clientX && event.clientX <= rect.left + rect.width);
      if (!isInDialog) {
        closeButtonAction();
      }
    });

    dialog.appendChild(dialogHeader);
    dialog.appendChild(dialogBody);
    dialog.appendChild(dialogFooter);
    dialogFooter.appendChild(closeButton);
    document.body.appendChild(dialog);

    return dialog;
  }

  // Examples:
  // url = 'http://google.com/hello?foo=bar&boo=baz#anchor1'
  // * remove: updateUrlQueryString(url, 'foo') => 'http://google.com/hello?boo=baz#anchor1'
  // * update: updateUrlQueryString(url, 'foo', 'fall') => 'http://google.com/hello?foo=fall&boo=baz#anchor1'
  // * insert: updateUrlQueryString(url, 'fooz', 'ball') => 'http://google.com/hello?foo=bar&boo=baz&fooz=ball#anchor1'
  #updateUrlQueryString(url, key, value) {
    const tmpUrl = new URL(url);
    const params = new URLSearchParams(tmpUrl.search);

    if (typeof value === "undefined" || value === null) {
      params.delete(key);
    } else {
      params.set(key, value);
    }

    // Reconstruct the URL with the updated query string
    tmpUrl.search = params.toString();
    return tmpUrl.toString();
  }
}

export default ActiveTimer;
